'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  title?: string;
  text: string;
  className?: string;
}

const TOOLTIP_WIDTH = 256;
const EDGE_GAP = 8;

interface Position {
  left: number;
  top?: number;
  bottom?: number;
}

// Rendered in a portal with fixed positioning so it isn't clipped by the
// horizontally scrolling screener table.
export default function InfoTooltip({ title, text, className = '' }: InfoTooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const tooltipId = useId();

  const open = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const centred = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    const left = Math.min(Math.max(centred, EDGE_GAP), window.innerWidth - TOOLTIP_WIDTH - EDGE_GAP);
    const fitsBelow = rect.bottom + 180 < window.innerHeight;
    setPosition(
      fitsBelow
        ? { left, top: rect.bottom + EDGE_GAP }
        : { left, bottom: window.innerHeight - rect.top + EDGE_GAP },
    );
  }, []);

  const close = () => setPosition(null);

  const isOpen = position !== null;

  useEffect(() => {
    if (!isOpen) return;
    // Follow the icon when the page or the table scrolls underneath it.
    const reposition = () => open();
    const handleOutside = (event: Event) => {
      if (!triggerRef.current?.contains(event.target as Node)) setPosition(null);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('pointerdown', handleOutside);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('pointerdown', handleOutside);
    };
  }, [isOpen, open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={title ? `What is ${title}?` : 'More info'}
        aria-describedby={position ? tooltipId : undefined}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={(event) => {
          // Headers are sortable; a tap on the info icon should only explain.
          event.stopPropagation();
          event.preventDefault();
          open();
        }}
        className={`inline-flex shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors hover:text-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-zinc-500 dark:hover:text-emerald-400 ${className}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>

      {position &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{ position: 'fixed', width: TOOLTIP_WIDTH, ...position }}
            className="pointer-events-none z-[60] rounded-xl border border-zinc-200 bg-white p-3 text-left text-xs normal-case leading-relaxed tracking-normal text-zinc-600 shadow-xl dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
          >
            {title && <p className="mb-1 text-sm font-semibold text-zinc-900 dark:text-white">{title}</p>}
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
